import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BusinessCard = ({ business, onClick, onConnect }) => {
  const getCategoryIcon = (category) => {
    const iconMap = {
      technology: 'Cpu',
      transportation: 'Car',
      energy: 'Zap',
      water_management: 'Droplets',
      waste_management: 'Trash2',
      governance: 'Building',
      education: 'BookOpen',
      healthcare: 'Heart',
      safety_security: 'Shield',
      economic_development: 'TrendingUp'
    };
    return iconMap?.[category] || 'Building2';
  };

  const formatCategory = (category) => {
    return category?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text?.length > maxLength ? `${text?.substring(0, maxLength)}...` : text;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {business?.logo_url ? (
              <img
                src={business?.logo_url}
                alt={`${business?.name} logo`}
                className="w-12 h-12 rounded-lg object-cover border border-border"
              />
            ) : (
              <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                <Icon 
                  name={getCategoryIcon(business?.category)} 
                  size={24} 
                  className="text-muted-foreground"
                />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
              {business?.name}
            </h3>
            <p className="text-sm text-muted-foreground">
              {formatCategory(business?.category)}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
          {business?.is_featured && (
            <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
              Featured
            </div>
          )}
          {business?.is_verified && (
            <Icon name="CheckCircle" size={16} className="text-green-500" />
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
        {truncateText(business?.description, 120)}
      </p>

      {/* Services */}
      {business?.services?.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {business?.services?.slice(0, 3)?.map((service, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200"
              >
                {service}
              </span>
            ))}
            {business?.services?.length > 3 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                +{business?.services?.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Location and Contact */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          {business?.city && (
            <div className="flex items-center space-x-1">
              <Icon name="MapPin" size={14} />
              <span>{business?.city}, {business?.country}</span>
            </div>
          )}
          <div className="flex items-center space-x-1">
            <Icon name="Eye" size={14} />
            <span>{business?.view_count || 0} views</span>
          </div>
        </div>
      </div>

      {/* Technologies */}
      {business?.technologies?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-2">Technologies:</p>
          <div className="flex flex-wrap gap-1">
            {business?.technologies?.slice(0, 4)?.map((tech, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-50 text-green-700 border border-green-200"
              >
                {tech}
              </span>
            ))}
            {business?.technologies?.length > 4 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                +{business?.technologies?.length - 4}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center space-x-2 pt-4 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e?.stopPropagation();
            onClick(business);
          }}
          iconName="Eye"
          iconPosition="left"
          iconSize={14}
          className="flex-1"
        >
          View Details
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={(e) => {
            e?.stopPropagation();
            onConnect(business);
          }}
          iconName="MessageSquare"
          iconPosition="left"
          iconSize={14}
          className="flex-1"
        >
          Connect
        </Button>
      </div>

      {/* Website Link */}
      {business?.website_url && (
        <div className="mt-3 pt-3 border-t border-border">
          <a
            href={business?.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-1 text-sm text-primary hover:text-primary/80 transition-colors"
            onClick={(e) => e?.stopPropagation()}
          >
            <Icon name="ExternalLink" size={14} />
            <span>Visit Website</span>
          </a>
        </div>
      )}
    </div>
  );
};

export default BusinessCard;